import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BACKEND_URL } from '@/config/api';
import { xhrFetchAuth } from '@/lib/xhrFetch';
import UploadProgressModal from './UploadProgressModal';
import StreamlinedTTSProgressModal from './StreamlinedTTSProgressModal';
import SimpleIngestGate1Form from './SimpleIngestGate1Form';
import SimpleIngestOutlineEditor from './SimpleIngestOutlineEditor';

const API = `${BACKEND_URL}/api`;

/**
 * StreamlinedUploadFlow - Production upload flow for authors
 * 
 * FLOW: Each gate shows a modal with spinner during processing.
 * When gate completes, modal shows checkmark and Continue button.
 * User must click Continue to advance to next gate.
 * 
 * NOTE: Uses xhrFetchAuth instead of fetch to bypass rrweb interceptor
 * that corrupts response bodies on non-OK responses.
 */
const StreamlinedUploadFlow = ({ 
  isOpen, 
  onClose, 
  onComplete,
  // Publisher managed author configuration
  apiBaseUrl = null,  // Custom API base URL (e.g., /api/publisher/managed-authors/{id}/simple-ingest)
  publisherAuthorId = null,
  publisherAuthorName = null,
  isPublisherManagedAuthor = false
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Use custom API base or default simple-ingest
  const getApiBase = () => apiBaseUrl || `${API}/simple-ingest`;
  
  // Flow state
  const [currentStep, setCurrentStep] = useState('metadata');
  
  const [jobId, setJobId] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // STEP 1: Force render trigger to fix React state update issues
  const [forceRender, setForceRender] = useState(0);
  
  // Gate completion states
  const [gate1Complete, setGate1Complete] = useState(false);
  const [gate2Complete, setGate2Complete] = useState(false);
  const [gate4Complete, setGate4Complete] = useState(false);
  const [gate5Complete, setGate5Complete] = useState(false);
  
  // Gate 6 TTS state
  const [gate6Progress, setGate6Progress] = useState(null);
  const [gate6PollInterval, setGate6PollInterval] = useState(null);

  // STEP 4: useEffect to watch state changes and trigger re-renders
  useEffect(() => {
    console.log('[StreamlinedUpload] useEffect triggered - currentStep changed to:', currentStep);
    console.log('[StreamlinedUpload] Completion states:', {
      gate1Complete,
      gate2Complete,
      gate4Complete,
      gate5Complete,
      forceRender
    });
  }, [currentStep, gate1Complete, gate2Complete, gate4Complete, gate5Complete, forceRender]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (gate6PollInterval) {
        clearInterval(gate6PollInterval);
      }
    };
  }, [gate6PollInterval]);

  // ============================================
  // STEP 1: Create Job and Upload (Gate 1)
  // ============================================
  const handleMetadataSubmit = async (formDataObj) => {
    setError(null);
    setIsSubmitting(true);
    setGate1Complete(false);
    
    const title = formDataObj.get('manuscript-title');
    setMetadata({ title_name: title });
    
    try {
      console.log('[StreamlinedUpload] Creating new job...');
      const createResult = await xhrFetchAuth(`${getApiBase()}/jobs`, { 
        method: 'POST'
      });
      
      if (!createResult.ok) {
        throw new Error(createResult.json?.message || createResult.json?.detail || 'Failed to create upload job');
      }
      
      const job = createResult.json?.data || createResult.json;
      const newJobId = job.job_id;
      setJobId(newJobId);
      
      console.log('[StreamlinedUpload] Job created:', newJobId);
      
      setCurrentStep('gate1');
      console.log('[StreamlinedUpload] currentStep set to: gate1');
      
      console.log('[StreamlinedUpload] Uploading files to Gate 1...');
      const uploadResult = await xhrFetchAuth(`${getApiBase()}/jobs/${newJobId}/gate-1`, {
        method: 'POST',
        body: formDataObj
      });
      
      if (!uploadResult.ok) {
        throw new Error(uploadResult.json?.message || uploadResult.json?.detail || 'Failed to upload manuscript');
      }
      
      if (!uploadResult.json?.ok) {
        throw new Error(uploadResult.json?.message || uploadResult.json?.error_code || 'Upload failed');
      }
      
      console.log('[StreamlinedUpload] Gate 1 complete - showing Continue button');
      setGate1Complete(true);
      console.log('[StreamlinedUpload] gate1Complete set to: true');
      
    } catch (err) {
      console.error('[StreamlinedUpload] Upload error:', err);
      setError(err.message);
      setCurrentStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gate 1 Continue Handler
  const handleGate1Continue = () => {
    console.log('[StreamlinedUpload] User clicked Continue after Gate 1');
    processGate2(jobId);
  };

  // ============================================
  // GATE 2: Segmentation
  // ============================================
  const processGate2 = async (jobIdParam) => {
    try {
      setCurrentStep('gate2');
      setGate2Complete(false);
      console.log('[StreamlinedUpload] currentStep set to: gate2');
      
      console.log('[StreamlinedUpload] Processing Gate 2 (Segmentation)...');
      
      const result = await xhrFetchAuth(
        `${getApiBase()}/jobs/${jobIdParam}/gate-2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!result.ok) {
        throw new Error(result.json?.message || result.json?.detail || 'Segmentation failed');
      }
      
      if (!result.json?.ok && result.json?.ok !== undefined) {
        throw new Error(result.json?.message || result.json?.error_code || 'Segmentation failed');
      }
      
      console.log('[StreamlinedUpload] Gate 2 complete - showing Continue button');
      setGate2Complete(true);
      console.log('[StreamlinedUpload] gate2Complete set to: true');
      
    } catch (err) {
      console.error('[StreamlinedUpload] Gate 2 error:', err);
      setError(err.message);
      setCurrentStep('error');
    }
  };

  // Gate 2 Continue Handler
  const handleGate2Continue = () => {
    console.log('[StreamlinedUpload] User clicked Continue after Gate 2 - opening outline editor');
    setCurrentStep('gate3');
    console.log('[StreamlinedUpload] currentStep set to: gate3');
  };

  // ============================================
  // STEP 2: GATE 3 Complete Handler - Fixed with forceRender
  // ============================================
  const handleGate3Continue = async () => {
    console.log('[StreamlinedUpload] Gate 3 (outline editor) complete, proceeding to Gate 4');
    console.log('[StreamlinedUpload] About to call processGate4 with jobId:', jobId);
    
    try {
      await processGate4(jobId);
      
      // Force a re-render after processGate4 completes
      console.log('[StreamlinedUpload] processGate4 completed, forcing re-render');
      setForceRender(prev => prev + 1);
      
    } catch (err) {
      console.error('[StreamlinedUpload] Error in processGate4:', err);
      setError(err.message);
      setCurrentStep('error');
    }
  };

  // ============================================
  // STEP 3: GATE 4 with setTimeout for state updates
  // ============================================
  const processGate4 = async (jobIdParam) => {
    console.log('[StreamlinedUpload] processGate4 called with jobId:', jobIdParam);
    
    // Set step FIRST before any async operations
    setCurrentStep('gate4');
    setGate4Complete(false);
    console.log('[StreamlinedUpload] currentStep set to: gate4');
    
    try {
      console.log('[StreamlinedUpload] Processing Gate 4 (Manifest)...');
      const result = await xhrFetchAuth(
        `${getApiBase()}/jobs/${jobIdParam}/gate-4/manifest`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!result.ok) {
        throw new Error(result.json?.message || result.json?.detail || 'Manifest generation failed');
      }
      
      console.log('[StreamlinedUpload] Gate 4 complete - showing Continue button');
      
      // Use setTimeout to ensure React processes the state update
      setTimeout(() => {
        console.log('[StreamlinedUpload] Setting gate4Complete to true');
        setGate4Complete(true);
        console.log('[StreamlinedUpload] gate4Complete set, forcing re-render');
        setForceRender(prev => prev + 1);
      }, 100);
      
    } catch (err) {
      console.error('[StreamlinedUpload] Gate 4 error:', err);
      setError(err.message);
      setCurrentStep('error');
    }
  };

  // Gate 4 Continue Handler
  const handleGate4Continue = () => {
    console.log('[StreamlinedUpload] User clicked Continue after Gate 4');
    processGate5(jobId);
  };

  // ============================================
  // STEP 3: GATE 5 with setTimeout for state updates
  // ============================================
  const processGate5 = async (jobIdParam) => {
    console.log('[StreamlinedUpload] processGate5 called with jobId:', jobIdParam);
    
    setCurrentStep('gate5');
    setGate5Complete(false);
    console.log('[StreamlinedUpload] currentStep set to: gate5');
    
    try {
      console.log('[StreamlinedUpload] Processing Gate 5 (R2 Upload)...');
      const result = await xhrFetchAuth(
        `${getApiBase()}/jobs/${jobIdParam}/gate-5/upload-to-r2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 180000
        }
      );
      
      if (!result.ok) {
        throw new Error(result.json?.message || result.json?.detail || result.error?.message || 'R2 upload failed');
      }
      
      console.log('[StreamlinedUpload] Gate 5 complete - showing Continue button');
      
      // Use setTimeout to ensure React processes the state update
      setTimeout(() => {
        console.log('[StreamlinedUpload] Setting gate5Complete to true');
        setGate5Complete(true);
        console.log('[StreamlinedUpload] gate5Complete set, forcing re-render');
        setForceRender(prev => prev + 1);
      }, 100);
      
    } catch (err) {
      console.error('[StreamlinedUpload] Gate 5 error:', err);
      setError(err.message);
      setCurrentStep('error');
    }
  };

  // Gate 5 Continue Handler
  const handleGate5Continue = () => {
    console.log('[StreamlinedUpload] User clicked Continue after Gate 5');
    processGate6(jobId);
  };

  // ============================================
  // GATE 6: TTS Generation with Polling
  // ============================================
  const processGate6 = async (jobIdParam) => {
    console.log('[StreamlinedUpload] processGate6 called with jobId:', jobIdParam);
    
    // CRITICAL: Set currentStep FIRST to show modal immediately
    setCurrentStep('gate6');
    console.log('[StreamlinedUpload] currentStep set to: gate6 - modal should appear now');
    
    try {
      console.log('[StreamlinedUpload] Starting Gate 6 (TTS Generation)...');
      
      // STEP 1: Fetch job data to get segment count from Gate 2 outline
      console.log('[StreamlinedUpload] Fetching job data to get segment count...');
      const jobResult = await xhrFetchAuth(
        `${getApiBase()}/jobs/${jobIdParam}`,
        {}
      );
      
      let totalSegments = 10; // Default fallback
      if (jobResult.ok) {
        const jobData = jobResult.json?.data || jobResult.json;
        const outline = jobData.gate2?.outline || [];
        totalSegments = outline.length || 10;
        console.log('[StreamlinedUpload] Total segments from Gate 2 outline:', totalSegments);
      }
      
      // STEP 2: Initialize progress state with zero values BEFORE starting TTS
      // This makes the modal show "0/X segments" immediately
      const initialProgress = {
        status: 'processing',
        total_segments: totalSegments,
        completed_count: 0,
        pending_count: totalSegments,
        failed_count: 0,
        total_cost: 0.0,
        estimated_time_remaining: null
      };
      
      console.log('[StreamlinedUpload] 🎬 Setting initial progress state:', initialProgress);
      setGate6Progress(initialProgress);
      console.log('[StreamlinedUpload] ✅ Initial progress state set: 0/' + totalSegments + ' segments');
      
      // Small delay to ensure modal renders with initial state
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // STEP 3: Trigger TTS generation (fire and forget - backend processes in background)
      console.log('[StreamlinedUpload] 🚀 Triggering TTS generation...');
      xhrFetchAuth(
        `${getApiBase()}/jobs/${jobIdParam}/gate-6/generate-audio`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[StreamlinedUpload] ✅ TTS generation triggered, starting progress polling...');
      
      // STEP 4: Start polling for progress immediately (every 2 seconds)
      const interval = setInterval(async () => {
        console.log('[StreamlinedUpload] 🔄 POLL EXECUTED at', new Date().toISOString());
        
        try {
          const progressResult = await xhrFetchAuth(
            `${getApiBase()}/jobs/${jobIdParam}/gate-6/status`,
            {}
          );
          
          console.log('[StreamlinedUpload] 📡 Response status:', progressResult.status);
          console.log('[StreamlinedUpload] 📡 Response OK:', progressResult.ok);
          
          if (progressResult.ok) {
            console.log('[StreamlinedUpload] 📦 Raw API result:', progressResult.json);
            
            // CRITICAL: Check if result has the progress data
            const progress = progressResult.json?.data || progressResult.json;
            console.log('[StreamlinedUpload] 📊 Extracted progress:', progress);
            console.log('[StreamlinedUpload] 📈 Progress details:', {
              completed: progress.completed_count,
              total: progress.total_segments,
              status: progress.status,
              pending: progress.pending_count
            });
            
            // Update state
            setGate6Progress(progress);
            console.log('[StreamlinedUpload] ✅ State updated with progress');
            
            const completed = progress.completed_count || 0;
            const total = progress.total_segments || 0;
            const tts = progress.tts || {};
            const failReason = tts.fail_reason || progress.fail_reason;
            const lastError = tts.last_error;
            const crashError = tts.crash_error;
            const crashedAt = tts.crashed_at;
            
            console.log(`[StreamlinedUpload] Gate 6: ${completed}/${total} segments, status: ${progress.status}, fail_reason: ${failReason}`);
            
            // CRITICAL: Detect crashed state - background task died
            if (progress.status === 'crashed' || tts.state === 'CRASHED') {
              console.error('[StreamlinedUpload] ❌ Gate 6 CRASHED: Background task died');
              clearInterval(interval);
              setGate6PollInterval(null);
              setGate6Progress({
                ...progress,
                status: 'crashed',
                crash_error: crashError,
                crashed_at: crashedAt,
                tts: {
                  ...tts,
                  state: 'CRASHED',
                  crash_error: crashError
                }
              });
              return;
            }
            
            // CRITICAL: Detect hard failures - 0 completed is a FAIL, not "completed_with_failures"
            const isHardFail = 
              progress.status === 'failed' || 
              tts.state === 'FAIL' || 
              failReason === 'NO_AUDIO_GENERATED' ||
              (total > 0 && completed === 0 && (progress.status === 'completed_with_failures' || progress.status === 'completed'));
            
            if (isHardFail) {
              console.error('[StreamlinedUpload] ❌ Gate 6 HARD FAIL: No audio generated');
              clearInterval(interval);
              setGate6PollInterval(null);
              // Update progress to reflect the failure state clearly
              setGate6Progress({
                ...progress,
                status: 'failed',
                fail_reason: failReason || 'NO_AUDIO_GENERATED',
                last_error: lastError,
                tts: {
                  ...tts,
                  state: 'FAIL',
                  fail_reason: failReason || 'NO_AUDIO_GENERATED'
                }
              });
              return;
            }
            
            if (progress.status === 'completed' || progress.status === 'completed_with_failures') {
              console.log('[StreamlinedUpload] 🎉 Gate 6 complete! Stopping polling.');
              clearInterval(interval);
              setGate6PollInterval(null);
            }
          } else {
            console.error('[StreamlinedUpload] ❌ Response not OK:', progressResult.status);
            console.error('[StreamlinedUpload] ❌ Error response:', progressResult.rawText);
          }
        } catch (pollError) {
          console.error('[StreamlinedUpload] ❌ Error polling progress:', pollError);
          console.error('[StreamlinedUpload] ❌ Error details:', pollError.message);
        }
      }, 2000);
      
      console.log('[StreamlinedUpload] ⏱️ Polling interval started, ID:', interval);
      setGate6PollInterval(interval);
      
    } catch (err) {
      console.error('[StreamlinedUpload] ❌ Gate 6 error:', err);
      setError(err.message);
      setCurrentStep('error');
    }
  };

  // Gate 6 Retry Handler - Force resumes TTS from where it stopped (even if stalled)
  const handleGate6Retry = async () => {
    console.log('[StreamlinedUpload] User clicked Retry TTS - force resuming from where it stopped...');
    
    try {
      // Clear any existing poll interval
      if (gate6PollInterval) {
        clearInterval(gate6PollInterval);
        setGate6PollInterval(null);
      }
      
      // Update progress to show "resuming" state
      setGate6Progress(prev => ({
        ...prev,
        status: 'processing',
        current_segment: 'Resuming...'
      }));
      
      // Use force-resume endpoint to bypass "already in_progress" check
      // This handles stalled jobs where the worker died but status is still in_progress
      console.log('[StreamlinedUpload] 🔄 Force-resuming TTS generation...');
      const result = await xhrFetchAuth(
        `${getApiBase()}/jobs/${jobId}/gate-6/force-resume`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!result.ok) {
        console.error('[StreamlinedUpload] Force-resume failed:', result.json);
        setError(result.json?.message || 'Failed to resume TTS generation');
        return;
      }
      
      console.log('[StreamlinedUpload] ✅ TTS force-resumed successfully, starting polling...');
      
      // Start polling for progress updates
      const interval = setInterval(async () => {
        try {
          const progressResult = await xhrFetchAuth(
            `${getApiBase()}/jobs/${jobId}/gate-6/status`,
            {}
          );
          
          if (progressResult.ok) {
            const progress = progressResult.json?.data || progressResult.json;
            
            setGate6Progress(progress);
            
            const completed = progress.completed_count || 0;
            const total = progress.total_segments || 0;
            console.log(`[StreamlinedUpload] Gate 6 Retry: ${completed}/${total} segments, status: ${progress.status}`);
            
            if (progress.status === 'completed' || progress.status === 'completed_with_failures') {
              console.log('[StreamlinedUpload] 🎉 Gate 6 complete after retry!');
              clearInterval(interval);
              setGate6PollInterval(null);
            } else if (progress.status === 'failed') {
              console.log('[StreamlinedUpload] ⚠️ Gate 6 failed again');
              // Don't clear interval - let user see the status and retry again if needed
            }
          }
        } catch (pollError) {
          console.error('[StreamlinedUpload] Error polling after retry:', pollError);
        }
      }, 2000);
      
      setGate6PollInterval(interval);
      
    } catch (err) {
      console.error('[StreamlinedUpload] Retry error:', err);
      setError(err.message);
    }
  };

  // Gate 6 Continue Handler
  const handleGate6Continue = async () => {
    try {
      console.log('[StreamlinedUpload] User clicked Continue after Gate 6 - creating manuscript...');
      
      // CRITICAL: Actually call the create-manuscript endpoint
      console.log('[StreamlinedUpload] Calling POST create-manuscript for job:', jobId);
      
      const result = await xhrFetchAuth(
        `${getApiBase()}/jobs/${jobId}/create-manuscript`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!result.ok) {
        console.error('[StreamlinedUpload] Failed to create manuscript:', result.json);
        throw new Error(result.json?.error || result.json?.message || `Failed to create manuscript: ${result.status}`);
      }
      
      console.log('[StreamlinedUpload] Manuscript created successfully:', result.json);
      const manuscriptId = result.json?.data?.manuscript_id || result.json?.manuscript_id || result.json?.id;
      const publisherTitleId = result.json?.data?.publisher_title_id || result.json?.publisher_title_id;
      console.log('[StreamlinedUpload] Manuscript ID:', manuscriptId);
      console.log('[StreamlinedUpload] Publisher Title ID:', publisherTitleId);
      
      // Notify parent to refresh manuscripts list
      console.log('[StreamlinedUpload] Notifying parent to refresh library...');
      if (onComplete) {
        onComplete({ manuscriptId, jobId, publisherTitleId });
      }
      
      // Small delay to allow library to refresh
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Navigate based on whether this is publisher-managed or independent author
      if (isPublisherManagedAuthor && publisherAuthorId) {
        // For publisher managed authors, navigate to the TITLE EDIT PAGE (like independent authors)
        // Use publisher_title_id from the response to go to /publisher/titles/{id}/edit
        if (publisherTitleId) {
          console.log('[StreamlinedUpload] Complete! Navigating to publisher title edit page:', publisherTitleId);
          navigate(`/publisher/titles/${publisherTitleId}/edit`);
        } else {
          // Fallback to titles list if no publisher_title_id (shouldn't happen)
          console.warn('[StreamlinedUpload] No publisher_title_id returned, falling back to titles list');
          navigate(`/publisher/authors/${publisherAuthorId}/titles`);
        }
      } else {
        // Navigate to the APPROVED title page (AuthorBookDetailsPage) for independent authors
        console.log('[StreamlinedUpload] Complete! Navigating to author title page...');
        navigate(`/author/titles/${manuscriptId}`);
      }
      
    } catch (err) {
      console.error('[StreamlinedUpload] Error creating manuscript:', err);
      setError('Failed to create manuscript: ' + err.message);
    }
  };

  // Handle Close/Cancel
  const handleClose = () => {
    if (gate6PollInterval) {
      clearInterval(gate6PollInterval);
      setGate6PollInterval(null);
    }
    
    setCurrentStep('metadata');
    setJobId(null);
    setMetadata({});
    setGate6Progress(null);
    setError(null);
    setIsSubmitting(false);
    setGate1Complete(false);
    setGate2Complete(false);
    setGate4Complete(false);
    setGate5Complete(false);
    setForceRender(0);
    
    if (onClose) {
      onClose();
    }
  };

  // Handle retry from error state
  const handleRetry = () => {
    setError(null);
    setCurrentStep('metadata');
  };

  // =============================================================================
  // PHASE 4: Handle Close with Partial Progress
  // Saves partial progress and navigates to title page
  // =============================================================================
  const handleCloseWithPartial = async () => {
    if (!jobId) {
      console.error('[StreamlinedUpload] Cannot close with partial: No job ID');
      alert('Unable to save progress: Job ID not found.');
      return;
    }

    try {
      console.log('[StreamlinedUpload] User clicked Close with Partial - saving progress...');
      
      // Call close-with-partial endpoint
      const result = await xhrFetchAuth(
        `${getApiBase()}/jobs/${jobId}/gate-6/close-with-partial`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!result.ok) {
        console.error('[StreamlinedUpload] Close with partial failed:', result.json);
        alert(result.json?.message || 'Failed to save partial progress. Please try again.');
        return;
      }
      
      console.log('[StreamlinedUpload] Partial progress saved:', result.json);
      
      // CRITICAL: Check for manuscriptId in response
      const manuscriptId = result.json?.data?.manuscriptId;
      const authorId = result.json?.data?.authorId || publisherAuthorId;
      
      if (result.json?.ok && manuscriptId) {
        // Stop polling
        if (gate6PollInterval) {
          clearInterval(gate6PollInterval);
          setGate6PollInterval(null);
        }
        
        console.log('[StreamlinedUpload] Manuscript created:', manuscriptId);
        console.log('[StreamlinedUpload] Navigating to title page...');
        
        // CRITICAL: Navigate directly to the title's edit page using window.location
        // This ensures navigation happens even if React Router has issues
        if (isPublisherManagedAuthor && authorId) {
          // For publishers, go directly to the manuscript edit page
          window.location.href = `/publisher/authors/${authorId}/titles/${manuscriptId}/edit`;
        } else {
          // For independent authors
          window.location.href = `/author/titles/${manuscriptId}`;
        }
        
        // Don't call onComplete - we're navigating away
        return;
      } else {
        // No manuscript ID returned - something went wrong
        console.error('[StreamlinedUpload] No manuscriptId in response:', result.json);
        alert('Manuscript was not created. Please try again or contact support.');
      }
      
    } catch (error) {
      console.error('[StreamlinedUpload] Error closing with partial:', error);
      alert('An error occurred while saving progress. Please try again.');
    }
  };

  // ============================================
  // RENDER
  // ============================================
  // CRITICAL FIX: Only return null if:
  // 1. isOpen is false AND
  // 2. We're still on metadata step (not in the middle of an upload)
  // This prevents the component from unmounting during the upload flow
  if (!isOpen && currentStep === 'metadata') {
    return null;
  }

  // Debug logging for render
  console.log('[StreamlinedUpload] Render - currentStep:', currentStep, 'forceRender:', forceRender, 'isOpen:', isOpen);
  console.log('[StreamlinedUpload] Completion states:', {
    gate1Complete,
    gate2Complete,
    gate4Complete,
    gate5Complete
  });

  return (
    <>
      {/* Metadata Form */}
      {currentStep === 'metadata' && (
        <SimpleIngestGate1Form
          isOpen={true}
          onClose={handleClose}
          onSubmit={handleMetadataSubmit}
          isSubmitting={isSubmitting}
          isModal={true}
          publisherAuthorName={publisherAuthorName}
        />
      )}
      
      {/* Gate 1: Uploading */}
      {currentStep === 'gate1' && (
        <>
          {console.log('[StreamlinedUpload] Rendering Gate 1 modal, isComplete:', gate1Complete)}
          <UploadProgressModal
            isOpen={true}
            title="Uploading Your EPUB File Now"
            message="Please wait while we upload your manuscript to our servers..."
            isComplete={gate1Complete}
            onContinue={handleGate1Continue}
          />
        </>
      )}
      
      {/* Gate 2: Segmentation */}
      {currentStep === 'gate2' && (
        <>
          {console.log('[StreamlinedUpload] Rendering Gate 2 modal, isComplete:', gate2Complete)}
          <UploadProgressModal
            isOpen={true}
            title="Book Segmentation is Processing"
            message="Analyzing your manuscript structure and breaking it into chapters..."
            isComplete={gate2Complete}
            onContinue={handleGate2Continue}
          />
        </>
      )}
      
      {/* STEP 6: Gate 3: Outline Editor - Verify onComplete prop is passed */}
      {currentStep === 'gate3' && jobId && (
        <>
          {console.log('[StreamlinedUpload] Rendering Gate 3 outline editor, jobId:', jobId, 'apiBaseUrl:', getApiBase())}
          <SimpleIngestOutlineEditor
            isOpen={true}
            jobId={jobId}
            onClose={handleClose}
            onComplete={handleGate3Continue}
            apiBaseUrl={getApiBase()}
          />
        </>
      )}
      
      {/* STEP 5: Gate 4: Manifest Generation - with detailed logging */}
      {currentStep === 'gate4' && (
        <>
          {console.log('[StreamlinedUpload] ATTEMPTING to render Gate 4 modal')}
          {console.log('[StreamlinedUpload] gate4Complete is:', gate4Complete)}
          <UploadProgressModal
            isOpen={true}
            title="Playback Manifest is Being Generated"
            message="Creating the audio playback structure for your manuscript..."
            isComplete={gate4Complete}
            onContinue={handleGate4Continue}
          />
        </>
      )}
      
      {/* STEP 5: Gate 5: R2 Upload - with detailed logging */}
      {currentStep === 'gate5' && (
        <>
          {console.log('[StreamlinedUpload] ATTEMPTING to render Gate 5 modal')}
          {console.log('[StreamlinedUpload] gate5Complete is:', gate5Complete)}
          <UploadProgressModal
            isOpen={true}
            title="NFD SmartBook Segments Being Generated"
            message="Preparing your manuscript segments for audio generation..."
            isComplete={gate5Complete}
            onContinue={handleGate5Continue}
          />
        </>
      )}
      
      {/* Gate 6: TTS Progress */}
      {currentStep === 'gate6' && (
        <>
          {console.log('[StreamlinedUpload] Rendering Gate 6 TTS modal')}
          <StreamlinedTTSProgressModal
            isOpen={true}
            bookTitle={metadata.title_name || 'Your Book'}
            progress={gate6Progress}
            onContinue={handleGate6Continue}
            onRetry={handleGate6Retry}
            onCloseWithPartial={handleCloseWithPartial}
          />
        </>
      )}
      
      {/* Error State */}
      {currentStep === 'error' && (
        <UploadProgressModal
          isOpen={true}
          title="Upload Failed"
          message=""
          error={error || 'An unexpected error occurred'}
          onContinue={handleRetry}
        />
      )}
    </>
  );
};

export default StreamlinedUploadFlow;
